import axios from 'axios';

const API_BASE_URL =
  process.env.REACT_APP_API_URL ||
  (process.env.NODE_ENV === 'development' ? 'http://localhost:5001/api' : '/api');

// Create axios instance
const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30000, // Increased to 30 seconds for matches API
  headers: {
    'Content-Type': 'application/json',
    'API-Version': '1.0',
    'Accept': 'application/json',
  },
});

// Request interceptor
api.interceptors.request.use(
  (config) => {
    console.log(`API Request: ${config.method?.toUpperCase()} ${config.url}`);
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor
api.interceptors.response.use(
  (response) => {
    return response.data;
  },
  (error) => {
    console.error('API Error:', error.response?.data || error.message);
    return Promise.reject(error.response?.data || error);
  }
);

// API Service class
class ApiService {
  // Health and Info
  async getHealth() {
    return api.get('/health');
  }

  async getInfo() {
    return api.get('/info');
  }

  // Countries
  async getCountries() {
    return api.get('/countries');
  }

  async getCountryById(id) {
    return api.get(`/countries/${id}`);
  }

  // Leagues
  async getLeagues(params = {}) {
    return api.get('/leagues', { params });
  }

  async getTopLeagues(limit = 10) {
    return api.get('/leagues/top', { params: { limit } });
  }

  async getLeagueById(id) {
    return api.get(`/leagues/${id}`);
  }

  async getLeaguesByCountry(country) {
    return api.get(`/leagues/country/${country}`);
  }

  // Teams
  async getTeams(params = {}) {
    return api.get('/teams', { params });
  }

  async getTeamById(id) {
    return api.get(`/teams/${id}`);
  }

  async getTeamsByLeague(leagueId) {
    return api.get(`/teams/league/${leagueId}`);
  }

  async getTeamsByCountry(country) {
    return api.get(`/teams/country/${country}`);
  }

  async searchTeams(query, limit = 10) {
    return api.get('/teams/search', { params: { q: query, limit } });
  }

  // Matches
  async getLiveMatches() {
    return api.get('/matches/live');
  }

  async getTodayMatches(params = {}) {
    return api.get('/matches', { params });
  }

  async getAllMatches(params = {}) {
    return api.get('/matches', { params });
  }

  // New league-based matches API
  async getLeagueMatches(params = {}) {
    return api.get('/leagues/matches', { params });
  }

  async getScores(params = {}) {
    return api.get('/scores', { params });
  }

  async getTomorrowMatches() {
    return api.get('/matches/tomorrow');
  }

  async getMatchById(id) {
    return api.get(`/matches/${id}`);
  }

  // Get individual match details (using the new singular route)
  async getMatchDetails(id) {
    return api.get(`/match/${id}`);
  }

  async getMatchesByLeague(leagueId, params = {}) {
    return api.get(`/matches/league/${leagueId}`, { params });
  }

  async getMatchesByTeam(teamId, params = {}) {
    return api.get(`/matches/team/${teamId}`, { params });
  }

  async getMatchesByDateRange(startDate, endDate, params = {}) {
    return api.get('/matches', { 
      params: { 
        start_date: startDate, 
        end_date: endDate, 
        ...params 
      } 
    });
  }

  async searchMatches(query, limit = 10) {
    return api.get('/matches/search', { params: { q: query, limit } });
  }

  async getMatchStatistics(id) {
    return api.get(`/matches/${id}/statistics`);
  }

  // Admin functions
  async getSchedulerStatus() {
    return api.get('/admin/scheduler/status');
  }

  async triggerLiveUpdate() {
    return api.post('/admin/scheduler/trigger-live');
  }

  async triggerFixtureUpdate() {
    return api.post('/admin/scheduler/trigger-fixtures');
  }

  async getWebSocketStats() {
    return api.get('/admin/websocket/stats');
  }

  // Anonymous Streaming (No login required)
  async getAnonymousStreamUrl(channelId) {
    return api.get(`/anonymous/generate/${channelId}`);
  }

  async getPublicChannels() {
    return api.get('/anonymous/public/channels');
  }

  // Channels (Today's Match Channels)  
  async getChannelsPageData() {
    return api.get('/channels');
  }

  // Search for a specific channel
  async searchChannel(channelName) {
    return api.get(`/search/${encodeURIComponent(channelName)}`);
  }

  // Stream a channel
  async getChannelStream(channelName, metadata = false) {
    const params = metadata ? '?metadata=true' : '';
    return api.get(`/channel/${encodeURIComponent(channelName)}${params}`);
  }

  // Get all unified provider channels
  async getAllUnifiedChannels(limit = 1000) {
    return api.get('/providers/channels/all', { params: { limit } });
  }

  // HLS Proxy methods for CORS-enabled streaming
  async getProxiedStreamUrl(streamUrl) {
    const encodedUrl = encodeURIComponent(streamUrl);
    return `${API_BASE_URL}/hls/proxy/${encodedUrl}`;
  }

  async getChannelStreamProxied(channelName) {
    const streamData = await this.getChannelStream(channelName);
    if (streamData && streamData.streamUrl) {
      return {
        ...streamData,
        proxiedUrl: await this.getProxiedStreamUrl(streamData.streamUrl),
        originalUrl: streamData.streamUrl
      };
    }
    return streamData;
  }

  async getHlsProxyHealth() {
    return api.get('/hls/health');
  }

  async fetchSubscriptionStatus(guestId) {
    return api.get('/billing/status', {
      params: { guestId },
      headers: guestId ? { 'X-Guest-Id': guestId } : {}
    });
  }

  async getSiteSettings() {
    return api.get('/settings');
  }

  async createCheckoutSession(plan, guestId) {
    return api.post('/billing/create-checkout-session', {
      plan,
      guestId
    });
  }

  async verifyCheckoutSession(sessionId, guestId) {
    return api.post('/billing/verify-session', {
      sessionId,
      guestId
    });
  }

  async createBillingPortalSession(token, returnUrl) {
    return api.post(
      '/billing/portal',
      { returnUrl },
      { headers: token ? { Authorization: `Bearer ${token}` } : {} }
    );
  }
}

// Create and export instance
const apiService = new ApiService();

export default apiService;

// Export individual methods for convenience
export const {
  getHealth,
  getInfo,
  getCountries,
  getLeagues,
  getTopLeagues,
  getTeams,
  getLiveMatches,
  getTodayMatches,
  getTomorrowMatches,
  searchMatches,
  searchTeams
} = apiService;
